/**
 * Phase 2.10 — Framework/standard pill resolution for assessment findings.
 *
 * Infrastructure Security findings (source_type === 'infrastructure_security')
 * use the same NIST + telecom standard mappings exposed by the Infrastructure
 * Control info popup (INFRASTRUCTURE_CONTROL_METADATA).
 *
 * Questionnaire-driven findings keep the existing "NIST · {function}" badge.
 *
 * Display order priority (Phase 2.10):
 *   1. NIST CSF        (PR.*, GV.*, ID.*, DE.*, RS.*, RC.*)
 *   2. GSMA
 *   3. 3GPP
 *   4. ENISA
 *   5. ETSI
 *   6. ISO / IEC
 *   7. RFC / IEEE / Other
 */
import { INFRASTRUCTURE_CONTROL_METADATA } from "@/configs/controlGuidance/infrastructureControlMetadata";

export type FrameworkFamily =
  | "NIST"
  | "GSMA"
  | "3GPP"
  | "ENISA"
  | "ETSI"
  | "ISO"
  | "RFC"
  | "IEEE"
  | "Other";

export interface FrameworkPill {
  label: string;
  family: FrameworkFamily;
}

const FAMILY_PRIORITY: Record<FrameworkFamily, number> = {
  NIST: 1,
  GSMA: 2,
  "3GPP": 3,
  ENISA: 4,
  ETSI: 5,
  ISO: 6,
  RFC: 7,
  IEEE: 7,
  Other: 8,
};

function classifyStandard(raw: string): FrameworkFamily {
  const s = raw.trim().toUpperCase();
  if (/^(PR|GV|ID|DE|RS|RC)\./.test(s)) return "NIST";
  if (s.startsWith("NIST")) return "NIST";
  if (s.startsWith("GSMA")) return "GSMA";
  if (s.startsWith("3GPP")) return "3GPP";
  if (s.startsWith("ENISA")) return "ENISA";
  if (s.startsWith("ETSI")) return "ETSI";
  if (s.startsWith("ISO") || s.startsWith("IEC")) return "ISO";
  if (s.startsWith("RFC")) return "RFC";
  if (s.startsWith("IEEE")) return "IEEE";
  return "Other";
}

export interface FindingFrameworkInput {
  source_type?: string | null;
  source_key?: string | null;
  infrastructure_control_key?: string | null;
  nist_function?: string | null;
}

export function resolveFindingFrameworkPills(
  f: FindingFrameworkInput,
): FrameworkPill[] {
  const infraKey = f.source_key || f.infrastructure_control_key || null;
  const isInfra = f.source_type === "infrastructure_security" || !!infraKey;

  if (isInfra && infraKey) {
    const meta = INFRASTRUCTURE_CONTROL_METADATA[infraKey];
    if (!meta) return [];
    const all: FrameworkPill[] = [];
    const seen = new Set<string>();
    const push = (raw: string) => {
      const v = raw.trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      all.push({ label: v, family: classifyStandard(v) });
    };
    // NIST first (already classified), then telecom standards.
    for (const n of meta.nistMappings ?? []) push(n.startsWith("NIST") ? n : `NIST ${n}`);
    for (const t of meta.telecomStandards ?? []) push(t);

    all.sort((a, b) => {
      const pa = FAMILY_PRIORITY[a.family];
      const pb = FAMILY_PRIORITY[b.family];
      if (pa !== pb) return pa - pb;
      return a.label.localeCompare(b.label);
    });
    return all;
  }

  // Questionnaire finding: preserve existing NIST · Function pill behaviour.
  if (f.nist_function && String(f.nist_function).trim()) {
    return [{ label: `NIST · ${f.nist_function}`, family: "NIST" }];
  }
  return [];
}
